import React from 'react'

import {
  BasicType,
  Column,
  Field,
} from '../../../src/components/forms/FormElementParts'
import FormErrorMessage from '../../../src/components/forms/FormErrorMessage'
import styled, { MQ_TABLET } from '../../../src/constants/theme'
import useForm, { FormData } from '../../../src/hooks/useForm'
import { responsivePaddingStyles } from '../../../styles'
import { PageHeader } from '../pages/PageParts'
import FooterDiv from '../elements/FooterDiv'
import FieldFactory, {
  Form,
  getModalOpenName,
  ServerError,
} from './FieldFactory'

interface State {
  [key: string]: boolean
}

interface Props {
  additionalFields?: Field[]
  description?: string
  form: Form
  isSubmitting?: boolean
  onChange?: (formData: FormData) => void
  onClickBack?: () => void
  onSubmit: (formData: FormData) => void
  serverError: ServerError
  showFieldIcons?: boolean
  showRequiredFieldsExplanation?: boolean
  width?: string
}

const FormBuilder: React.FC<Props> = (props) => {
  const { onFormElementChange, formData } = useForm()
  const {
    additionalFields,
    description,
    form,
    isSubmitting,
    onChange,
    onClickBack,
    onSubmit,
    serverError,
    showFieldIcons,
    showRequiredFieldsExplanation,
    width,
  } = props
  const { fields, header } = form
  const initState = {}
  fields.forEach((field) => {
    if (field.hint) {
      initState[getModalOpenName(field)] = false
    }
  })
  const [state, setState] = React.useState<State>(initState)

  const { message } = serverError
  const footerFields = fields.filter(
    (field: Field) => field.type === BasicType.FOOTER,
  )
  let newFields = fields
  if (additionalFields) {
    newFields = []
    fields.forEach((field) => {
      if (field.type === 'submit') {
        newFields.push(...additionalFields)
      }
      newFields.push(field)
    })
  }

  const focusOnFirstField = React.useCallback(() => {
    const focusable: any = document.querySelector('input, select')
    focusable?.focus()
  }, [])

  React.useEffect(() => {
    focusOnFirstField()
  }, [focusOnFirstField])

  React.useEffect(() => {
    if (serverError.message) {
      focusOnFirstField() // When a form was not submitted successfully and an error message is revealed, it is important a11y functionality to return the user to the first field in the form (this field is also programatically labelled by the error)
    }
  }, [focusOnFirstField, serverError])

  const handleClickBack = React.useCallback(() => {
    onClickBack?.()
  }, [onClickBack])

  const handleCloseModal = (field: Field) => () => {
    setState({ ...state, [getModalOpenName(field)]: false })
  }

  const handleOpenModal = (field: Field) => () => {
    setState({ ...state, [getModalOpenName(field)]: true })
  }

  const handleSubmitForm = (e: React.FormEvent<HTMLFormElement>) => {
    e.currentTarget.querySelector('input')?.blur() // When focus is on the first field and a user presses `enter` to submit the form; re-focus is needed on that first field so that the error message is read to screen readers
    e.preventDefault()
    e.stopPropagation()
    onSubmit(formData)
  }

  return (
    <React.Fragment>
      <Contents className="FormBuilder" width={width}>
        {header && <PageHeader>{header}</PageHeader>}
        {description && <p dangerouslySetInnerHTML={{ __html: description }} />}
        <FormErrorMessage htmlFor={newFields[0].name} message={message} />
        <FormEl onSubmit={handleSubmitForm}>
          <Column>
            <FieldFactory
              errorText={message || undefined}
              fields={newFields}
              formType={form.type}
              isSubmitting={isSubmitting}
              isValid={true}
              onChange={onChange}
              onClickBack={handleClickBack}
              onCloseModal={handleCloseModal}
              onFormElementChange={onFormElementChange}
              onOpenModal={handleOpenModal}
              serverError={serverError}
              showFieldIcons={showFieldIcons}
              showRequiredFieldsExplanation={showRequiredFieldsExplanation}
              state={state}
            />
          </Column>
        </FormEl>
      </Contents>
      {footerFields &&
        footerFields.length > 0 &&
        footerFields.map((field: Field) => (
          <FooterDiv
            className={field.type}
            dangerouslySetInnerHTML={{
              __html: field.label,
            }}
            key={field.label}
          />
        ))}
    </React.Fragment>
  )
}

const Contents = styled.div<{ width?: string }>(
  {
    maxWidth: '100%',
    overflow: 'hidden',
  },
  ({ theme, width }) => ({
    ...MQ_TABLET({
      paddingLeft: theme.padding,
      paddingRight: theme.padding,
    }),
    color: theme.text,
    paddingLeft: theme.paddingMobile,
    paddingRight: theme.paddingMobile,
    width: width || 500,
  }),
)

const FormEl = styled.form({}, ({ theme }) => responsivePaddingStyles(theme))

export default FormBuilder
